/**
 * SkyNexa — 3D interaction controller.
 *
 * Writes CSS custom properties only; every visual decision lives in
 * css/interactive-3d.css. Design constraints:
 *
 *   - One shared rAF loop. Pointer events fire far faster than the display
 *     refreshes, so handlers record state and the loop does the writing.
 *     Without this, a wall of cards produces hundreds of style writes a frame.
 *   - Effects are opt-in per element via data attributes, so a page carries
 *     only the behaviour it marks up.
 *   - Reduced motion and coarse pointers bail out before any listener is
 *     attached, rather than being neutralised later in CSS.
 *   - Scope is pointer tracking only. Scroll reveal and parallax live in
 *     js/motion.js, driven by GSAP; they are not duplicated here, because two
 *     systems writing transform on the same element is a race.
 *
 * @module interactive-3d
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)';

/** Clamp `value` into [min, max]. */
export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Map a pointer position over an element to a tilt rotation.
 *
 * Pointer at the top edge should tip the card's top edge *away* from the
 * viewer, which is a positive rotateX, hence the sign flip on the Y axis.
 *
 * @param {{ x: number, y: number }} pointer Pointer position in viewport px.
 * @param {{ left: number, top: number, width: number, height: number }} rect Element box.
 * @param {number} max Maximum rotation in degrees.
 * @returns {{ rx: number, ry: number, mx: number, my: number }} Rotations in
 *   degrees and the pointer position as a 0–100 percentage of the element.
 */
export function computeTilt(pointer, rect, max) {
  if (!rect.width || !rect.height) {
    return { rx: 0, ry: 0, mx: 50, my: 50 };
  }
  const px = clamp((pointer.x - rect.left) / rect.width, 0, 1);
  const py = clamp((pointer.y - rect.top) / rect.height, 0, 1);

  return {
    rx: +((0.5 - py) * 2 * max).toFixed(3),
    ry: +((px - 0.5) * 2 * max).toFixed(3),
    mx: +(px * 100).toFixed(2),
    my: +(py * 100).toFixed(2),
  };
}

/**
 * Offset a magnetic element toward the pointer, capped so it never detaches
 * from its layout position far enough to look broken.
 *
 * @returns {{ x: number, y: number }} Offset in px.
 */
export function computeMagnetism(pointer, rect, strength, maxTravel = 14) {
  const centreX = rect.left + rect.width / 2;
  const centreY = rect.top + rect.height / 2;
  return {
    x: +clamp((pointer.x - centreX) * strength, -maxTravel, maxTravel).toFixed(2),
    y: +clamp((pointer.y - centreY) * strength, -maxTravel, maxTravel).toFixed(2),
  };
}

/** Read a numeric custom property off an element, with a fallback. */
function cssNumber(element, property, fallback) {
  const raw = getComputedStyle(element).getPropertyValue(property).trim();
  const parsed = Number.parseFloat(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * A single requestAnimationFrame loop shared by every effect. Handlers push a
 * write function; the loop flushes them once per frame.
 */
function createFrameScheduler() {
  const pending = new Map();
  let frame = 0;

  const flush = () => {
    frame = 0;
    const writes = [...pending.values()];
    pending.clear();
    for (const write of writes) write();
  };

  return {
    schedule(key, write) {
      pending.set(key, write);
      if (!frame) frame = requestAnimationFrame(flush);
    },
    stop() {
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
      pending.clear();
    },
  };
}

/** Attach pointer-tracked tilt + spotlight to every `[data-tilt]` element. */
function initTilt(scheduler) {
  const cards = document.querySelectorAll('[data-tilt]');

  cards.forEach((card, index) => {
    const key = `tilt-${index}`;
    const max = cssNumber(card, '--tilt-max', 9);
    let rect = null;

    const onEnter = () => {
      rect = card.getBoundingClientRect();
      card.classList.add('is-tilting');
    };

    const onMove = (event) => {
      if (!rect) rect = card.getBoundingClientRect();
      const { rx, ry, mx, my } = computeTilt({ x: event.clientX, y: event.clientY }, rect, max);
      scheduler.schedule(key, () => {
        card.style.setProperty('--rx', `${rx}deg`);
        card.style.setProperty('--ry', `${ry}deg`);
        card.style.setProperty('--mx', `${mx}%`);
        card.style.setProperty('--my', `${my}%`);
      });
    };

    const onLeave = () => {
      rect = null;
      card.classList.remove('is-tilting');
      scheduler.schedule(key, () => {
        card.style.setProperty('--rx', '0deg');
        card.style.setProperty('--ry', '0deg');
        card.style.setProperty('--mx', '50%');
        card.style.setProperty('--my', '50%');
      });
    };

    card.addEventListener('pointerenter', onEnter);
    card.addEventListener('pointermove', onMove, { passive: true });
    card.addEventListener('pointerleave', onLeave);
    // Scrolling moves the element out from under the cached rect.
    window.addEventListener(
      'scroll',
      () => {
        rect = null;
      },
      { passive: true },
    );
  });

  return cards.length;
}

/** Attach cursor magnetism to every `[data-magnetic]` element. */
function initMagnetic(scheduler) {
  const targets = document.querySelectorAll('[data-magnetic]');

  targets.forEach((target, index) => {
    const key = `magnet-${index}`;
    const strength = cssNumber(target, '--magnet-strength', 0.28);

    const onMove = (event) => {
      const rect = target.getBoundingClientRect();
      const { x, y } = computeMagnetism({ x: event.clientX, y: event.clientY }, rect, strength);
      target.classList.add('is-magnetized');
      scheduler.schedule(key, () => {
        target.style.setProperty('--mag-x', `${x}px`);
        target.style.setProperty('--mag-y', `${y}px`);
      });
    };

    const onLeave = () => {
      target.classList.remove('is-magnetized');
      scheduler.schedule(key, () => {
        target.style.setProperty('--mag-x', '0px');
        target.style.setProperty('--mag-y', '0px');
      });
    };

    target.addEventListener('pointermove', onMove, { passive: true });
    target.addEventListener('pointerleave', onLeave);
    target.addEventListener('blur', onLeave);
  });

  return targets.length;
}

/**
 * Boot every effect. Safe to call more than once; effects that were skipped
 * because of user preference simply report zero.
 *
 * @returns {{ tilt: number, magnetic: number, motion: 'full'|'reduced' }}
 */
export function initInteractive3D() {
  const prefersReducedMotion = window.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
  const hasFinePointer = window.matchMedia?.(FINE_POINTER_QUERY).matches ?? false;

  if (prefersReducedMotion || !hasFinePointer) {
    return { tilt: 0, magnetic: 0, motion: 'reduced' };
  }

  const scheduler = createFrameScheduler();
  const result = {
    tilt: initTilt(scheduler),
    magnetic: initMagnetic(scheduler),
    motion: 'full',
  };

  // If the user switches on Reduce Motion mid-session, stop writing frames.
  window.matchMedia(REDUCED_MOTION_QUERY).addEventListener('change', (event) => {
    if (event.matches) scheduler.stop();
  });

  return result;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initInteractive3D(), { once: true });
  } else {
    initInteractive3D();
  }
}
